// MatchRecorder.js
// Buffers the live wire-event stream + periodic board snapshots into a
// versioned, replay-grade match log. Pure aside from the injected `now`
// clock. No DOM, no network, no globals. So it is fully unit-testable.

export const FORMAT_VERSION = 1;
export const MIN_MEANINGFUL_EVENTS = 5;
const DEFAULT_MAX_EVENTS = 20000;

// Pure presentation streams, excluded from the log. These are
// high-frequency cosmetic mirrors whose durable outcome is carried by a
// discrete event that IS recorded. Card:release carries a drag's final
// pose; hover / ping / lightning /
// assign-preview are transient by design. Recording them bloated real
// match logs to ~8 MB of drag-interpolation frames and burned most of
// the event cap on data no replay or analytics pass reads.
export const PRESENTATION_EVENT_TYPES = new Set([
  'card:pose',
  'card:hover',
  'ping:fire',
  'fx:lightning',
  'combat:assign_preview',
]);

export class MatchRecorder {
  constructor({ now = () => Date.now(), maxEvents = DEFAULT_MAX_EVENTS } = {}) {
    this.now = now;
    this.maxEvents = maxEvents;
    this._reset();
  }

  _reset() {
    this.started = false;
    this.startMs = 0;
    this.roomCode = null;
    this.seat = null;
    this.meta = {};
    this.initialSnapshot = null;
    this.events = [];
    this.keyframes = [];
    this.truncated = false;
  }

  start({ roomCode, seat, meta = {}, initialSnapshot = null }) {
    this._reset();
    this.started = true;
    this.startMs = this.now();
    this.roomCode = roomCode;
    this.seat = seat;
    this.meta = meta;
    this.initialSnapshot = initialSnapshot;
  }

  _t() {
    return this.now() - this.startMs;
  }

  recordEvent({ dir, type, data }) {
    if (!this.started) return;
    if (PRESENTATION_EVENT_TYPES.has(type)) return;
    if (this.events.length >= this.maxEvents) {
      this.truncated = true;
      return;
    }
    this.events.push({ t: this._t(), dir, type, data });
  }

  recordKeyframe(snapshot) {
    if (!this.started) return;
    this.keyframes.push({ t: this._t(), snapshot });
  }

  isMeaningful() {
    return this.events.length >= MIN_MEANINGFUL_EVENTS;
  }

  finalize() {
    const durationMs = this.events.length ? this.events[this.events.length - 1].t : 0;
    return {
      formatVersion: FORMAT_VERSION,
      roomCode: this.roomCode,
      seat: this.seat,
      recordedAt: this.now(),
      durationMs,
      truncated: this.truncated,
      meta: this.meta,
      initialSnapshot: this.initialSnapshot,
      events: this.events.slice(),
      keyframes: this.keyframes.slice(),
    };
  }
}
